// How the shared moments look (site/js/ui/moments.js says which one comes when; issue #132). Six kinds, drawn over the
// scene and never into it:
//   glow       a light swells on the landmark, holds, and goes; on a tower that sparkles, small lights twinkle up it
//   fireworks  eight small shells rise from behind the skyline and open beside the landmark
//   lanterns   paper lanterns drift up from the water and away
//   stars      a few shooting stars
//   flock      the city's birds cross the sky
//   flight     an airliner's lights cross it
// Every pose is a pure function of the moment (momentOf: where, and a seed) and of the seconds since its instant, so
// two pages draw the same frame at the same time; nothing here reads a clock. The first four go into the page's two
// overlay buffers in scene pixels (a glow added as light, ink on top), clipped to the city's boxes of open sky, so a
// shell opens behind a roof and not over it. The two that cross are put straight on the screen in its own pixels
// (display.sprites), so they glide at the screen's frame rate where a scene-pixel step would tick, and are hidden in the
// lane's gaps, column by column: they pass behind a tower or a window bar. With reduced motion (`still`) nothing
// flashes, flaps or blinks: the glow swells once and goes, lanterns rise without their flicker, birds and the flight
// glide. Tested in tests/moments.test.mjs.
import { W, H, TAU, clamp, smooth, hash, bayer, hex, makeCanvas, makeHalo, drawHalo } from '../engine/core.js';
import { ART as LANTERN_ART, PALETTE as LANTERN_PALETTE } from '../live/lanterns.js';
import { KINDS, FORMS } from './moments.js';

const NONE = Object.freeze([]);
// what a moment draws from (its shells, its birds…) is worked out once: moments are kept by momentOf
const planner = make => { const kept = new WeakMap(); return m => { let p = kept.get(m); if (!p) kept.set(m, p = make(m)); return p; }; };
/** #rrggbb between two of them */
const blend = (a, b, t) => { const A = hex(a), B = hex(b); return '#' + A.map((v, i) => Math.round(v + (B[i] - v) * t).toString(16).padStart(2, '0')).join(''); };
const pick = (m, i, salt) => hash(m.seed, i, salt);
/** the sky box a moment's i-th thing goes in: every other one in the first (the one a phone shows), the rest spread */
const boxOf = (m, i) => (i % 2 === 0 || m.sky.length < 2 ? m.sky[0] : m.sky[1 + Math.floor(pick(m, i, 90) * (m.sky.length - 1))]);

/* ================= glow ================= */
export const GLOW_IN = 5, GLOW_OUT = 8;             // seconds it takes to come up, and to go
/** How bright the glow is `age` seconds in, 0..1: up, a slow breath while it holds (none when `still`), and away. */
export function glowLevel(age, still = false, life = KINDS.glow.life) {
  if (!(age >= 0) || age >= life) return 0;
  const env = smooth(0, GLOW_IN, age) * (1 - smooth(life - GLOW_OUT, life, age));
  return still ? env : env * (0.92 + 0.08 * Math.sin(TAU * age / 6.5));
}

export const TWINKLES = 30;
/**
 * The lights twinkling on a landmark that sparkles (`m.sparkle`, its box) `age` seconds in: [{ x, y, a, big }], whole
 * pixels, tighter towards the box's top as a tower is; none when `still`. Each comes on and goes within a second,
 * somewhere else every time.
 */
export function twinkles(m, age) {
  const level = glowLevel(age);
  if (!m.sparkle || m.still || level <= 0) return NONE;
  const [bx, by, bw, bh] = m.sparkle, out = [];
  for (let i = 0; i < TWINKLES; i++) {
    const q = age / (0.4 + 0.5 * pick(m, i, 1)) + pick(m, i, 2), k = Math.floor(q), u = q - k, at = i * 977 + k;
    const v = hash(m.seed, at, 3), half = (bw / 2) * (0.1 + 0.9 * v);
    out.push({
      x: Math.round(bx + (bw - 1) / 2 + (hash(m.seed, at, 4) * 2 - 1) * half), y: Math.round(by + v * (bh - 1)),
      a: Math.sin(Math.PI * u) ** 2 * level, big: hash(m.seed, at, 5) < 0.14,
    });
  }
  return out;
}

const glows = new Map();                            // "rx,ry,colour,…" -> a dithered ellipse of light
function makeGlow(rx, ry, col, maxA, pow) {
  const key = `${rx},${ry},${col},${maxA},${pow}`;
  if (glows.has(key)) return glows.get(key);
  const rgb = hex(col), w = rx * 2 + 1, h = ry * 2 + 1, levels = 6;
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const dd = Math.hypot((i - rx) / rx, (j - ry) / ry);
    if (dd >= 1) continue;
    const q = Math.floor(Math.pow(1 - dd, pow) * levels + bayer(i, j)) / levels;
    if (q <= 0) continue;
    const o = (j * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = Math.round(Math.min(1, q) * maxA * 255);
  }
  x.putImageData(img, 0, 0);
  glows.set(key, c);
  return c;
}
const WARM = '#ffe7b8', WHITE = '#fff7e6';
// a wide light in the city's own colour, and a warmer, tighter one on the landmark itself
const glowArt = planner(m => {
  const [, , rx, ry] = m.glow;
  return { wide: makeGlow(rx + 12, ry + 10, blend(m.accent, WARM, 0.55), 0.34, 1.5), core: makeGlow(Math.round(rx * 0.72), Math.round(ry * 0.72), WARM, 0.42, 1.9) };
});
function drawGlow(glow, ink, m, age) {
  const level = glowLevel(age, m.still);
  if (level <= 0.004) return false;
  const [cx, cy] = m.glow, { wide, core } = glowArt(m);
  // the light stays inside the window pane it is seen through, and off the billboard, which is lit by its own lamps
  const [x0, y0, x1, y1] = m.pane ?? [0, 0, W - 1, H - 1], sign = m.billboard;
  for (const c of [glow, ink]) {
    c.save();
    c.beginPath();
    c.rect(x0, y0, x1 - x0 + 1, y1 - y0 + 1);
    if (sign) c.rect(sign.x - 1, sign.y - 1, sign.w + 2, sign.h + 2);
    c.clip('evenodd');
  }
  glow.globalCompositeOperation = 'lighter';
  glow.globalAlpha = level;
  glow.drawImage(wide, cx - (wide.width >> 1), cy - (wide.height >> 1));
  glow.drawImage(core, cx - (core.width >> 1), cy - (core.height >> 1));
  glow.globalAlpha = 1;
  glow.globalCompositeOperation = 'source-over';
  for (const t of twinkles(m, age)) {
    ink.globalAlpha = t.a;
    ink.fillStyle = WHITE;
    ink.fillRect(t.x, t.y, 1, 1);
    if (t.big && t.a > 0.5) { ink.globalAlpha = t.a * 0.6; ink.fillRect(t.x - 1, t.y, 3, 1); ink.fillRect(t.x, t.y - 1, 1, 3); }
  }
  ink.globalAlpha = 1;
  glow.restore(); ink.restore();
  return true;
}

/** Clips both buffers to boxes [x0, y0, x1, y1] (edges included); false, and nothing to restore, for none. */
function clipTo(boxes, glow, ink) {
  if (!boxes.length) return false;
  for (const c of [glow, ink]) {
    c.save();
    c.beginPath();
    for (const [x0, y0, x1, y1] of boxes) c.rect(x0, y0, x1 - x0 + 1, y1 - y0 + 1);
    c.clip();
  }
  return true;
}

/* ================= fireworks ================= */
export const SHELLS = 12, RISE = 0.9, BLOOM = 2.8;  // shells a show, seconds a shell climbs, seconds its sparks last
// soft colours, each with the shade its sparks cool to; one shell in six takes the city's own
const SHELL_COLOURS = [['#ffe2a0', '#d99a3a'], ['#ffb0c0', '#c4567a'], ['#aef3d8', '#3fa58c'], ['#b6d0ff', '#4f74c8'], ['#fff6e6', '#c2b294']];
/**
 * A show's shells: [{ i, t, x, y, r, from, lean, col: [bright, cooled], willow }]. Shell i opens `t` seconds in at
 * (x, y), `r` pixels wide at its fullest, after climbing from row `from` (the foot of its sky box, so from behind
 * whatever the box stands on). One every two or three seconds, small ones first and last, so one or two are open at a
 * time; all of a shell stays inside its box but for the sparks' fall. A willow's sparks hang and fall further.
 */
export const shellsOf = planner(m => {
  if (!m.sky.length) return NONE;
  const out = [];
  for (let i = 0; i < SHELLS; i++) {
    const [x0, y0, x1, y1] = boxOf(m, i), bw = x1 - x0 + 1, bh = y1 - y0 + 1;
    const size = 0.55 + 0.45 * Math.sin(Math.PI * (i + 0.5) / SHELLS);
    const r = Math.round(clamp((9 + 9 * pick(m, i, 2)) * size + 4, 6, Math.max(6, Math.min(20, bw / 2 - 1, bh * 0.48))));
    const c = Math.floor(pick(m, i, 7) * (SHELL_COLOURS.length + 1));
    out.push({
      i, t: 1.5 + i * 2.5 + pick(m, i, 5) * 1.2, r,
      x: Math.round(x0 + r + pick(m, i, 3) * Math.max(0, bw - 1 - 2 * r)),
      y: Math.round(y0 + r + pick(m, i, 4) * Math.max(0, bh * 0.62 - 2 * r)),
      from: y1 + 1, lean: Math.round((pick(m, i, 6) - 0.5) * 8),
      col: SHELL_COLOURS[c] ?? [blend(m.accent, '#ffffff', 0.35), blend(m.accent, '#000000', 0.3)],
      willow: pick(m, i, 8) < 0.3,
    });
  }
  return out;
});
/** How many sparks a shell throws. */
export const sparkCount = s => 18 + Math.round(s.r * 1.3);
/**
 * Spark j of shell `s`, `u` (0..1) through its bloom: { x, y, a } (fractions of a pixel; a: how bright, 0 once it is
 * out). They fly out fast and slow down, every third one half as far, sink a little, and flicker as they go out.
 */
export function sparkAt(m, s, j, u) {
  const at = s.i * 64 + j, n = sparkCount(s);
  const angle = TAU * (j + 0.7 * hash(m.seed, at, 11)) / n, reach = (j % 3 === 0 ? 0.55 : 1) * (0.82 + 0.18 * hash(m.seed, at, 12));
  const d = s.r * reach * (1 - (1 - u) ** 3);
  const fade = 1 - smooth(s.willow ? 0.62 : 0.5, 1, u), flicker = u > 0.55 && hash(m.seed, at, 13 + Math.floor(u * 26)) < 0.3 ? 0.4 : 1;
  return { x: s.x + Math.cos(angle) * d, y: s.y + Math.sin(angle) * d * 0.94 + (s.willow ? 11 : 6) * u * u, a: fade * flicker };
}
const flashes = new Map();                          // "r,colour" -> the soft light where a shell opens
function drawFireworks(glow, ink, m, age) {
  let drawn = false;
  for (const s of shellsOf(m)) {
    const a = age - s.t;
    if (a < -RISE || a >= BLOOM) continue;
    drawn = true;
    if (a < 0) {                                      // on its way up: a faint point and the tail behind it
      const p = 1 + a / RISE, e = 1 - (1 - p) ** 2, x = Math.round(s.x + s.lean * (1 - e)), y = s.from + (s.y - s.from) * e;
      ink.fillStyle = '#ffe9b8';
      for (let k = 0; k < 3; k++) { ink.globalAlpha = 0.55 * (1 - k / 3) * Math.min(1, p * 4); ink.fillRect(x, Math.round(y) + k * 2, 1, 1); }
      continue;
    }
    const u = a / BLOOM, key = `${s.r},${s.col[0]}`;
    if (!flashes.has(key)) flashes.set(key, makeHalo(s.r + 10, s.col[0], 0.5, 1.5));
    drawHalo(glow, flashes.get(key), s.x, s.y, 0.85 * (1 - u) ** 2.2);
    ink.fillStyle = u < 0.1 ? WHITE : u < 0.6 ? s.col[0] : s.col[1];
    for (let j = 0, n = sparkCount(s); j < n; j++) {
      const p = sparkAt(m, s, j, u);
      if (p.a <= 0.02) continue;
      ink.globalAlpha = p.a;
      ink.fillRect(Math.round(p.x), Math.round(p.y), 1, 1);
      for (let k = 1, tail = s.willow ? 4 : 2; k <= tail && u - k * 0.04 > 0 && u < 0.8; k++) {   // where it just was, dimmer
        const q = sparkAt(m, s, j, u - k * 0.04);
        ink.globalAlpha = p.a * (0.6 - k * 0.13);
        ink.fillRect(Math.round(q.x), Math.round(q.y), 1, 1);
      }
    }
  }
  ink.globalAlpha = 1;
  return drawn;
}

/* ================= lanterns ================= */
export const LANTERNS = 9, LANTERN_S = 30;          // lanterns a flight, seconds each is in the air
const LANTERN_SIZES = [2, 3, 4];                    // the far end of the Together lanterns' sizes (site/js/live/lanterns.js ART): 9x11, 7x8, 5x7
const LANTERN_HALOS = [[18, 8], [14, 6], [11, 5]];
/**
 * A flight's lanterns: [{ i, t, x, from, top, colour }]. Lantern i is let go `t` seconds in (the last within a quarter
 * of a minute) at column x, by the foot of its sky box, where it fades in as it starts to climb, and levels off at row
 * `top`. Next-door lanterns in time are apart in space.
 */
export const lanternsOf = planner(m => {
  if (!m.sky.length) return NONE;
  const out = [];
  for (let i = 0; i < LANTERNS; i++) {
    const [x0, y0, x1, y1] = m.sky[i % m.sky.length], bw = x1 - x0 + 1, bh = y1 - y0 + 1, place = (i * 4) % LANTERNS;
    out.push({
      i, t: i * 1.7 + pick(m, i, 21) * 1.2,
      x: Math.round(x0 + 8 + (place + pick(m, i, 22)) / LANTERNS * Math.max(0, bw - 16)),
      from: y1 - 2, top: Math.round(y0 + 5 + pick(m, i, 23) * bh * 0.3), colour: Math.floor(pick(m, i, 24) * LANTERN_PALETTE.length),
    });
  }
  return out;
});
/**
 * Where lantern `l` is `age` seconds into the flight: { x, y, size, alpha, glow } (x, y: its flame, whole pixels; size:
 * 0 near to 2 far), or null before it is let go and after it has burnt out. It slows as it climbs, sways, shrinks
 * twice and fades over its last seconds; its light flickers unless `still`.
 */
export function lanternAt(m, l, age) {
  const a = age - l.t;
  if (!(a >= 0) || a >= LANTERN_S) return null;
  const u = a / LANTERN_S, far = 1 - (1 - u) ** 2, k = m.seed + l.i;
  const sway = Math.sin(TAU * (a / (5 + 3 * hash(k, 31)) + hash(k, 32))) * 2.5 * (1 - 0.6 * u), wind = (hash(k, 33) - 0.5) * 22 * far;
  const alpha = smooth(0, 1.5, a) * (1 - smooth(LANTERN_S - 6, LANTERN_S, a));
  const flicker = m.still ? 0.9 : 0.84 + 0.1 * Math.sin(a * 7.3 + hash(k, 34) * 40) + 0.06 * Math.sin(a * 17.9 + hash(k, 35) * 40);
  return { x: Math.round(l.x + sway + wind), y: Math.round(l.from - (l.from - l.top) * far), size: u < 0.3 ? 0 : u < 0.65 ? 1 : 2, alpha, glow: alpha * flicker };
}
let lanternArt = null;                              // [colour][size] -> { sprite, halo, core }
function paintRows(rows, colours) {                 // a sprite from rows of letters, each a colour (`.`: clear)
  const [c, x] = makeCanvas(rows[0].length, rows.length);
  rows.forEach((row, j) => [...row].forEach((ch, i) => {
    if (!colours[ch]) return;
    x.fillStyle = colours[ch];
    x.fillRect(i, j, 1, 1);
  }));
  return c;
}
function drawLanternFlight(glow, ink, m, age) {
  let drawn = false;
  for (const l of lanternsOf(m)) {
    const p = lanternAt(m, l, age);
    if (!p || p.alpha <= 0.004) continue;
    lanternArt ??= LANTERN_PALETTE.map(([r, b, lit, f]) => LANTERN_SIZES.map((size, s) => ({
      sprite: paintRows(LANTERN_ART[size], { r, b, l: lit, f }), halo: makeHalo(LANTERN_HALOS[s][0], lit, 0.45), core: makeHalo(LANTERN_HALOS[s][1], f, 0.55, 1.6),
    })));
    const { sprite, halo, core } = lanternArt[l.colour][p.size], mid = p.y - (sprite.height >> 1);
    drawHalo(glow, halo, p.x, mid, p.glow);
    drawHalo(glow, core, p.x, mid, p.glow);
    ink.globalAlpha = p.alpha;
    ink.drawImage(sprite, p.x - (sprite.width >> 1), p.y - sprite.height + 1);
    drawn = true;
  }
  ink.globalAlpha = 1;
  return drawn;
}

/* ================= shooting stars ================= */
export const METEORS = 9, METEOR_S = 1;             // stars a shower, seconds each is seen
/**
 * A shower's stars: [{ i, t, x, y, dx, dy, len }]. Star i shows `t` seconds in, from (x, y) along the unit direction
 * (dx, dy), `len` pixels in all; the whole streak stays inside its sky box. They all fall the same way, one every three
 * seconds or so.
 */
export const meteorsOf = planner(m => {
  if (!m.sky.length) return NONE;
  const out = [], dir = pick(m, 0, 40) < 0.5 ? 1 : -1;
  for (let i = 0; i < METEORS; i++) {
    const [x0, y0, x1, y1] = boxOf(m, i), bw = x1 - x0 + 1, bh = y1 - y0 + 1, slope = 0.3 + 0.3 * pick(m, i, 41);
    const run = Math.max(8, Math.min(30 + 24 * pick(m, i, 42), bw - 6, (bh - 4) / slope)), drop = run * slope, len = Math.hypot(run, drop);
    const left = x0 + 2 + pick(m, i, 43) * Math.max(0, bw - 4 - run);
    out.push({
      i, t: 1.2 + i * 3 + pick(m, i, 44) * 1.4, x: dir > 0 ? left : left + run, y: y0 + 2 + pick(m, i, 45) * Math.max(0, bh - 4 - drop),
      dx: dir * run / len, dy: drop / len, len,
    });
  }
  return out;
});
/** Star `s`, `u` (0..1) through its fall: { x, y, tail, a }: its head, how long its tail is and how bright. */
export function meteorAt(s, u) {
  const d = s.len * u, swell = Math.sin(Math.PI * u);
  return { x: s.x + s.dx * d, y: s.y + s.dy * d, tail: Math.min(d, 4 + 12 * swell), a: Math.min(1, swell * 1.6) };
}
let meteorHalo = null;
function drawStars(glow, ink, m, age) {
  let drawn = false;
  for (const s of meteorsOf(m)) {
    const u = (age - s.t) / METEOR_S;
    if (!(u >= 0) || u >= 1) continue;
    const p = meteorAt(s, u);
    drawHalo(glow, meteorHalo ??= makeHalo(4, '#cfe0ff', 0.6), p.x, p.y, p.a);
    ink.fillStyle = '#eef4ff';
    for (let k = 0; k <= p.tail; k++) {
      ink.globalAlpha = p.a * (1 - k / (p.tail + 1)) ** 1.5;
      ink.fillRect(Math.round(p.x - s.dx * k), Math.round(p.y - s.dy * k), 1, 1);
    }
    drawn = true;
  }
  ink.globalAlpha = 1;
  return drawn;
}

/**
 * Draws an hour moment `m` (momentOf) `age` seconds after its instant into the two cleared overlay buffers: light into
 * `glow` (put on the screen additively), everything else into `ink`. Returns whether it drew anything.
 */
export function drawMoment(glow, ink, m, age) {
  if (!(age >= 0)) return false;
  if (m.kind === 'glow') return drawGlow(glow, ink, m, age);
  const draw = { fireworks: drawFireworks, lanterns: drawLanternFlight, stars: drawStars }[m.kind];
  if (!draw || !clipTo(m.sky, glow, ink)) return false;
  const drawn = draw(glow, ink, m, age);
  glow.restore(); ink.restore();
  return drawn;
}

/* ================= what crosses: a flock, a flight ================= */
// A bird in three wing beats, 5x4 (the small ones of a cloud of starlings: 3x3). x is the bird.
const BIRD = [['x...x', '.x.x.', '..x..', '.....'], ['.....', 'xx.xx', '..x..', '.....'], ['.....', '..x..', '.x.x.', 'x...x']];
const SMALL = [['x.x', '.x.', '...'], ['...', 'xxx', '...'], ['...', '.x.', 'x.x']];
const BEAT = [0, 1, 2, 1];                          // up, level, down, level
export const TINTS = { pale: '#e2e7f0', grey: '#aeb5c8', dusk: '#9097b6', green: '#9cc7a6', dark: '#15172b' };   // lit from below by the city; dark for a bright dusk sky
// An airliner from the side, nose to the right, 13x5: f fuselage, w a lit window, n nose, k fin, g wing
export const PLANE = ['k............', 'kk...........', 'ffwfwfwfwfff.', '.fffffffffffn', '.....gg......'];
const PLANE_COLOURS = { f: '#59648a', w: '#ffe6a6', n: '#7482ab', k: '#59648a', g: '#3d4668' };
// its lights, in the sprite's columns and rows: a red beacon on its back, a white strobe on the fin's tip, a steady
// landing light at the nose
export const PLANE_LIGHTS = { beacon: [5, 1], strobe: [0, 0], nose: [12, 3] };
const BEACON_EVERY = 1.4, BEACON_ON = 0.16;         // seconds

/**
 * The birds of a flock, in formation: [{ back, dy, phase, rate }] (back: whole pixels behind the leader; dy: rows off
 * the lane; rate: wing beats a second). A loose line of gulls, a V of geese, or a cloud of small birds.
 */
export const formationOf = planner(m => {
  const { n, span } = FORMS[m.form] ?? FORMS.loose, out = [];
  for (let i = 0; i < n; i++) {
    const a = pick(m, i, 51), b = pick(m, i, 52), bird = { phase: pick(m, i, 53), rate: 2.6 + 1.2 * pick(m, i, 54) };
    if (m.form === 'vee') { const k = Math.ceil(i / 2), side = i % 2 ? -1 : 1; Object.assign(bird, { back: k ? Math.round(6.5 * k + (a - 0.5) * 2) : 0, dy: side * k * (1.3 + 0.4 * b) }); }
    else if (m.form === 'cloud') { const at = a ** 0.8 * (span - 4); Object.assign(bird, { back: Math.round(at), dy: (b - 0.5) * 11 * (0.35 + 0.65 * Math.sin(Math.PI * at / span)), rate: 5 + 2 * pick(m, i, 54) }); }
    else Object.assign(bird, { back: i ? Math.round(i / (n - 1) * (span - 6) + (a - 0.5) * 6) : 0, dy: i ? (b - 0.5) * 10 : 0 });
    out.push(bird);
  }
  return out;
});

/**
 * What crosses, `age` seconds after its moment's instant (before it too: it sets off early, to be over the middle of
 * the scene's frame at the instant): { x, y, kind, dir, gaps, … } with x the leader's (or the nose's) column, to a
 * fraction of a pixel, and y the lane's row; or null once all of it is off the scene. A flock: + birds [{ ox, oy,
 * beat }] (ox whole pixels from x, oy rows from y, to a fraction; beat 0 up, 1 level, 2 down), small, tint. A flight:
 * + beacon, strobe (lit or not). `still`: wings level, no bobbing, the beacon steady and no strobe.
 */
export function passerPose(m, age) {
  if (m.slot !== 'half') return null;
  const x = m.anchor + m.dir * m.speed * age;
  if (m.dir > 0 ? x < -5 || x - m.span > W + 5 : x > W + 5 || x + m.span < -5) return null;
  const pose = { x, y: m.y, kind: m.kind, dir: m.dir, gaps: m.gaps, still: m.still };
  if (m.kind === 'flight') {
    const beat = ((age % BEACON_EVERY) + BEACON_EVERY) % BEACON_EVERY;
    return Object.assign(pose, { beacon: m.still || beat < BEACON_ON, strobe: !m.still && ((beat >= 0.7 && beat < 0.76) || (beat >= 0.86 && beat < 0.92)) });
  }
  const small = m.form === 'cloud', half = small ? 1 : 2;
  pose.small = small; pose.tint = m.tint ?? 'pale';
  pose.birds = formationOf(m).map((b, i) => {
    const glide = !small && hash(m.seed, i, 60 + Math.floor(age / 1.7 + b.phase * 3)) < 0.3;
    const bob = m.still ? 0 : (small ? 1.6 : 1.2) * Math.sin(TAU * (age / (small ? 1.9 : 2.6) + b.phase));
    return { ox: -m.dir * b.back - half, oy: b.dy + bob - 1, beat: m.still || glide ? 1 : BEAT[Math.floor(Math.abs(age * b.rate + b.phase * 4)) % 4] };
  });
  return pose;
}

/** The scene columns [x0, x1] a pose's sprites reach, for knowing whether a view shows any of it. */
export function passerSpan(m, pose) {
  return pose.dir > 0 ? [pose.x - m.span, pose.x] : [pose.x, pose.x + m.span];
}

/**
 * `put` (display.sprites') that leaves out the lane's gaps: a piece is put a run of columns at a time, and a column
 * whose middle is in a gap [x0, x1] (edges included) is skipped. `x`: the origin's scene column.
 */
export function gapped(put, x, gaps) {
  if (!gaps.length) return put;
  return (img, sx, sy, sw, sh, ox, oy, op) => {
    for (let c = 0, run = -1; c <= sw; c++) {
      const mid = x + ox + c + 0.5, hid = c === sw || gaps.some(([a, b]) => mid >= a && mid < b + 1);
      if (!hid) { if (run < 0) run = c; continue; }
      if (run >= 0) put(img, sx + run, sy, c - run, sh, ox + run, oy, op);
      run = -1;
    }
  };
}

let passerArt = null;                               // sprites, drawn on first use
function buildPassers() {
  const birds = {};
  for (const [tint, col] of Object.entries(TINTS)) birds[tint] = { big: BIRD.map(rows => paintRows(rows, { x: col })), small: SMALL.map(rows => paintRows(rows, { x: col })) };
  const right = paintRows(PLANE, PLANE_COLOURS), [left, lx] = makeCanvas(right.width, right.height);
  lx.translate(right.width, 0); lx.scale(-1, 1); lx.drawImage(right, 0, 0);
  const dot = col => paintRows(['x'], { x: col });
  return {
    birds, planes: { 1: right, [-1]: left },
    red: dot('#ff5a4a'), white: dot('#ffffff'), warm: dot('#fff0c8'),
    redHalo: makeHalo(4, '#ff5a4a', 0.8), whiteHalo: makeHalo(4, '#ffffff', 0.75), warmHalo: makeHalo(5, '#fff0c8', 0.7),
  };
}

/** Draws a pose (passerPose) with `put` (display.sprites', its origin at the pose's x, y). */
export function drawPasser(put, pose) {
  passerArt ??= buildPassers();
  const out = gapped(put, pose.x, pose.gaps);
  if (pose.kind === 'flight') {
    const img = passerArt.planes[pose.dir], w = img.width, left = pose.dir > 0 ? -w : 0;   // its nose at the origin
    const at = ([c, r]) => [left + (pose.dir > 0 ? c : w - 1 - c), r - 3];
    const light = (spot, dotImg, halo) => {
      const [ox, oy] = at(spot), { c, r } = halo;
      out(dotImg, 0, 0, 1, 1, ox, oy);
      out(c, 0, 0, c.width, c.height, ox - r, oy - r, 'lighter');
    };
    out(img, 0, 0, w, img.height, left, -3);
    light(PLANE_LIGHTS.nose, passerArt.warm, passerArt.warmHalo);
    if (pose.beacon) light(PLANE_LIGHTS.beacon, passerArt.red, passerArt.redHalo);
    if (pose.strobe) light(PLANE_LIGHTS.strobe, passerArt.white, passerArt.whiteHalo);
    return;
  }
  const set = (passerArt.birds[pose.tint] ?? passerArt.birds.pale)[pose.small ? 'small' : 'big'];
  for (const b of pose.birds) { const img = set[b.beat]; out(img, 0, 0, img.width, img.height, b.ox, b.oy); }
}
